import React from "react";
import ReactDOM from "react-dom";

import cx from "classnames";
import * as redom from "redom";
import uuid from "uuid";

import ActionItemCompletionMenu, { type AssigneeCompletionData } from "~/actionItems/ActionItemCompletionMenu";
import { StatusCondition } from "~/actionItems/enum";
import * as dom from "~/lib/dom";
import useID from "~/lib/hooks/useID";

import CircleCheckbox from "./CircleCheckbox";
import { SparkledSquareCheckboxDashedBorder, SparkledSquareCheckboxSolidBorder } from "./SparkledSquareCheckbox";
import { SquareCheckboxDashedBorder, SquareCheckboxSolidBorder } from "./SquareCheckbox";

import goalCheckboxStyles from "./GoalCheckbox.module.css";
import styles from "./NoteCheckbox.module.css";

/**
 * - `large` is usually used in notes
 * - `medium` is usually used in the side panel
 * - `small` is used in the transcript modal and recap tab
 */
export type CheckboxSize = "large" | "medium" | "small";

function GoalCheckbox() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="1 0 18 24" fill="none" className={goalCheckboxStyles.svg}>
			<path
				d={
					"M10.0001 23C3.19814 12.2115 " +
					"2.3479 11.524 2.3479 8.61539C2.3479 4.4375 5.74887 " +
					"1 10.0001 1C14.1981 1 17.6522 4.4375 17.6522 " +
					"8.61539C17.6522 11.524 16.802 12.1058 10.0001 23Z"
				}
				className={goalCheckboxStyles.fill}
			/>
			<path
				d={
					"M10 0C4.98438 0 1 4.07812 1 9C1 12.375 " +
					"2.07812 13.4062 8.07812 22.9688C8.96875 24.375 10.9844 24.375 " +
					"11.875 22.9688C17.875 13.4062 19 12.375 19 9C19 4.03125 " +
					"14.9219 0 10 0ZM10 21.75C4 12.1875 3.25 11.5781 3.25 9C3.25 " +
					"5.29688 6.25 2.25 10 2.25C13.7031 2.25 16.75 5.29688 " +
					"16.75 9C16.75 11.5781 16 12.0938 10 21.75Z"
				}
				className={goalCheckboxStyles.goalStroke}
			/>
			<path
				d="M6 10.5047L8.37838 12.7143L14 7"
				className={cx(goalCheckboxStyles.checkmark, goalCheckboxStyles.stroke)}
			/>
		</svg>
	);
}

const goalCheckbox = GoalCheckbox();
const talkingPointCheckbox = (size: CheckboxSize) => CircleCheckbox({ size });
const actionItemCheckbox = (size: CheckboxSize) => SquareCheckboxSolidBorder({ size });
const linkedActionItemCheckbox = (size: CheckboxSize) => SquareCheckboxDashedBorder({ size });
const generatedActionItemCheckbox = (size: CheckboxSize) => SparkledSquareCheckboxSolidBorder({ size });
const linkedGeneratedActionItemCheckbox = (size: CheckboxSize) => SparkledSquareCheckboxDashedBorder({ size });

export interface NoteCheckboxProps extends Omit<React.HTMLProps<HTMLInputElement>, "type" | "checked" | "ref"> {
	type: "action-item" | "talking-point" | "goal";
	state: "checked" | "unchecked" | "indeterminate";
	checkboxSize: CheckboxSize;
	linked?: boolean;
	inputClassname?: string;
	aiGenerated?: boolean;
}

export default React.forwardRef<HTMLInputElement, NoteCheckboxProps>(function NoteCheckbox(
	{
		state,
		linked,
		type,
		className,
		inputClassname: propInputClassname,
		onMouseDown,
		checkboxSize,
		aiGenerated = false,
		...inputProps
	},
	ref,
) {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const inputID = useID();

	React.useEffect(() => {
		if (!inputRef.current) return;

		inputRef.current.indeterminate = state === "indeterminate";
	}, [inputRef, state]);

	React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current, [inputRef]);

	let checkbox = talkingPointCheckbox(checkboxSize);
	let inputClassname = styles.checkbox;
	let labelClassname = styles.customSvgWrapper;

	if (type === "action-item") {
		if (linked) {
			checkbox = (aiGenerated ? linkedGeneratedActionItemCheckbox : linkedActionItemCheckbox)(checkboxSize);
		} else {
			checkbox = (aiGenerated ? generatedActionItemCheckbox : actionItemCheckbox)(checkboxSize);
		}
	} else if (type === "goal") {
		inputClassname = goalCheckboxStyles.checkbox;
		labelClassname = goalCheckboxStyles.label;

		checkbox = goalCheckbox;
	}

	return (
		<span className={styles.container} onMouseDown={onMouseDown}>
			<input
				ref={inputRef}
				id={inputID}
				type="checkbox"
				checked={state === "checked"}
				className={cx(inputClassname, propInputClassname)}
				{...inputProps}
			/>
			<label htmlFor={inputID} className={cx(labelClassname, className, styles[checkboxSize])}>
				{checkbox}
			</label>
		</span>
	);
});

function renderSvg(el: JSX.Element): SVGElement {
	const rendered = dom.render(el);
	if (!(rendered instanceof SVGElement)) {
		throw new Error("Element provided is not an SVG. Make sure <svg> is the root element.");
	}
	return rendered;
}

const actionItemCheckboxEl = renderSvg(actionItemCheckbox("large"));
const linkedActionItemCheckboxEl = renderSvg(linkedActionItemCheckbox("large"));
const goalCheckboxEl = renderSvg(goalCheckbox);
const talkingPointCheckboxEl = renderSvg(talkingPointCheckbox("large"));
const generatedActionItemCheckboxEl = renderSvg(generatedActionItemCheckbox("large"));
const linkedGeneratedActionItemCheckboxEl = renderSvg(linkedGeneratedActionItemCheckbox("large"));
export interface NoteCheckboxData {
	type: "generated-action-item" | "action-item" | "talking-point" | "goal";
	state: "checked" | "unchecked" | "indeterminate";
	statusCondition: StatusCondition;
	assignees: Array<AssigneeCompletionData>;
	isUserAssignee: boolean;
	linked: boolean;
	disabled: boolean;
	onChange: (checked: boolean) => void;
	onToggleAssignee: (assignee: AssigneeCompletionData) => void;
	onAssignYourself: () => void;
	className: string;
	isSnippet?: boolean;
}

export class NoteCheckboxDOMComponent implements dom.DOMComponent<NoteCheckboxData> {
	el: HTMLElement;
	private checkbox: HTMLSpanElement;
	private input: HTMLInputElement;
	private label: HTMLLabelElement;
	private data?: NoteCheckboxData;
	private svg: SVGElement;
	private triggerWrapper?: HTMLElement;

	static template = redom.el(
		"span",
		{ class: styles.container },
		redom.el("input", {
			type: "checkbox",
			class: styles.checkbox,
		}),
		redom.el("label", { class: `${styles.customSvgWrapper} ${styles.large}` }, actionItemCheckboxEl.cloneNode(true)),
	);

	static completeByAllContainerTemplate = redom.el("div", { class: styles.buttonContainer });

	constructor(data: NoteCheckboxData) {
		this.checkbox = NoteCheckboxDOMComponent.template.cloneNode(true) as HTMLSpanElement;
		this.checkbox.onmousedown = this.checkbox.onfocus = e => {
			e.stopPropagation();
			e.preventDefault();
		};

		const [input, label] = this.checkbox.children;
		if (!(input instanceof HTMLInputElement) || !(label instanceof HTMLLabelElement)) {
			throw new Error("Invalid template");
		}
		this.input = input;
		this.label = label;
		this.input.id = this.label.htmlFor = uuid.v4();
		const onCheckboxChange = this.onCheckboxChange.bind(this);
		if (data.statusCondition === StatusCondition.ALL && data.assignees.length > 1 && !data.isUserAssignee) {
			this.triggerWrapper = NoteCheckboxDOMComponent.completeByAllContainerTemplate.cloneNode(true) as HTMLDivElement;
			ReactDOM.render(
				<ActionItemCompletionMenu
					trigger={<button className={styles.completionMenuTrigger} ref={ref => ref?.appendChild(this.checkbox)} />}
					assignees={data.assignees || []}
					showAssignYourself={!data.isUserAssignee}
					onToggle={data.onToggleAssignee}
					onAssignYourself={data.onAssignYourself}
					overrideOpening={data.isUserAssignee ? onCheckboxChange : undefined}
					disabled={data.disabled}
				/>,
				this.triggerWrapper,
			);
			this.el = this.triggerWrapper;
		} else {
			this.el = this.checkbox;
		}

		this.input.onchange = onCheckboxChange;

		const svg = label.firstElementChild;
		if (!(svg instanceof SVGElement)) throw new Error("Invalid template");
		this.svg = svg;

		this.update(data);
	}

	onCheckboxChange() {
		if (!this.data) return;

		// The value passed to onChange is what the new value should be. If the
		// checkbox was checked or indeterminate before, we want it to become
		// unchecked.
		this.input.indeterminate = false;
		this.input.checked = this.data.state === "unchecked";
		this.data.onChange(this.input.checked);
	}

	update(data: NoteCheckboxData) {
		this.input.checked = data.state === "checked";
		this.input.indeterminate = data.state === "indeterminate";
		this.input.disabled = data.disabled;

		if (data.className !== this.data?.className) {
			if (this.data) this.label.classList.remove(...this.data.className.split(" "));
			this.label.classList.add(...data.className.split(" "));
		}

		if (data.isSnippet !== this.data?.isSnippet) {
			this.input.classList.toggle(goalCheckboxStyles.isSnippet, data.isSnippet ?? false);
		}
		const singleAssigneeToMultipleAssignee = this.data && this.data.assignees.length <= 1 && data.assignees.length > 1;
		const multipleAssigneesToSingleAssignee = this.data && this.data.assignees.length > 1 && data.assignees.length <= 1;

		if (
			!this.data ||
			data.type !== this.data.type ||
			data.statusCondition !== this.data.statusCondition ||
			singleAssigneeToMultipleAssignee ||
			multipleAssigneesToSingleAssignee
		) {
			let newSvg: SVGElement;
			switch (data.type) {
				case "action-item":
					newSvg = (data.linked ? linkedActionItemCheckboxEl : actionItemCheckboxEl).cloneNode(true) as SVGElement;
					break;
				case "generated-action-item":
					newSvg = (data.linked ? linkedGeneratedActionItemCheckboxEl : generatedActionItemCheckboxEl).cloneNode(
						true,
					) as SVGElement;
					break;
				case "goal":
					this.input.classList.replace(styles.checkbox, goalCheckboxStyles.checkbox);
					this.label.classList.replace(styles.customSvgWrapper, goalCheckboxStyles.label);
					this.label.classList.remove(styles.large);

					newSvg = goalCheckboxEl.cloneNode(true) as SVGElement;
					break;
				case "talking-point":
					newSvg = talkingPointCheckboxEl.cloneNode(true) as SVGElement;
					break;
			}
			this.svg.replaceWith(newSvg);
			this.svg = newSvg;
		}

		if (data.statusCondition === StatusCondition.ALL && data.assignees.length > 1) {
			if (!this.triggerWrapper) {
				this.triggerWrapper = NoteCheckboxDOMComponent.completeByAllContainerTemplate.cloneNode(true) as HTMLDivElement;
			}
			const onCheckboxChange = this.onCheckboxChange.bind(this);
			ReactDOM.render(
				<ActionItemCompletionMenu
					trigger={<button className={styles.completionMenuTrigger} ref={ref => ref?.appendChild(this.checkbox)} />}
					assignees={data.assignees || []}
					showAssignYourself={!data.isUserAssignee}
					onToggle={data.onToggleAssignee}
					onAssignYourself={data.onAssignYourself}
					overrideOpening={data.isUserAssignee ? onCheckboxChange : undefined}
					disabled={data.disabled}
				/>,
				this.triggerWrapper,
			);
			this.el = this.triggerWrapper;
		} else {
			this.el = this.checkbox;
		}

		this.data = data;
	}
}
